import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, TextField } from "@mui/material";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { ISessionState } from "../../../../models/session/sessionState";
import { Translations } from "ui/Scripts/source/models/translations";
import { Dropdown } from "ui/Scripts/source/components/framework/Dropdown/Dropdown";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { IMessageTypeListItem } from "source/models/messageType/messageTypeListItem";
import { DatePicker } from "source/components/framework/DateTime/DatePicker";
import { Base } from "ui/Scripts/source/framework/base";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { IRecordUnit, RecordUnit } from "ui/Scripts/source/models/unit/recordUnit";
import { IMessageEditItemTargetCompanyGroup, MessageEditItemTargetCompanyGroup } from "source/models/message/messageEditItemTargetCompanyGroup";
import { UnitBlocks } from "source/components/unit/UnitBlocks";
import { MessageEditDialogCompanyGroupBlocks } from "../MessageEditDialogCompanyGroupBlocks/MessageEditDialogCompanyGroupBlocks";
import { AppCkEditor } from "source/components/framework/AppCkEditor";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

const MessageTypeDropDownLine = ({ item }: { item: IMessageTypeListItem }) => {
    if (!item) return null;
    return (
        <Box display="flex" sx={{ overflow: "hidden" }}>
            <AvatarImage
                id={item.id}
                name={item.name}
                image={item.image}
            />
            <Box display="flex" sx={{ marginLeft: ".5rem", overflow: "hidden" }}>
                {item.name}
            </Box>
        </Box>
    );
};

interface IMessageEditDialogGeneralInformationProps {
    sessionState: ISessionState;
    hasNormalCompanyGroups: boolean;
    hasFixedCompanyGroups: boolean;
    canUseAllCompanyGroup: boolean;
    isNew: boolean;
    companyId: string;
    messageId: string;
    title: string;
    content: string;
    messageTypeId: string;
    dateStr: string;
    startDateStr: string;
    endDateStr: string;
    unitId: string;
    requiresReceipt: boolean;
    systemAdminMessage: boolean;
    messageTargetUnits: IRecordUnit[];
    messageTargetCompanyGroups: IMessageEditItemTargetCompanyGroup[];
    messageTypes: IMessageTypeListItem[];
    senderUnits: IIdTitleNode[];
    targetUnits: IIdTitleNode[];
    isLockedToOtherUser: boolean;
    onChangeTitle: (title: string) => void;
    onChangeDateStr: (dateStr: string) => void;
    onChangeStartDateStr: (startDateStr: string) => void;
    onChangeEndDateStr: (endDateStr: string) => void;
    onChangeMessageType: (messageTypeId: string) => void;
    onChangeUnit: (unitId: string) => void;
    onChangeRequiresReceipt: (requiresReceipt: boolean) => void;
    onChangeSystemAdminMessage: (systemAdminMessage: boolean) => void;
    onChangeContent: (content: string, dirty: boolean) => void;
    onChangeMessageTargetUnits: (messageTargetUnits: IRecordUnit[]) => void;
    onChangeMessageTargetCompanyGroups: (messageTargetCompanyGroups: IMessageEditItemTargetCompanyGroup[]) => void;
    // onChangeHandlers: (handlers: IModifyUser[]) => void;
}

export const MessageEditDialogGeneralInformation = (props: IMessageEditDialogGeneralInformationProps) => {
    const [messageType, setMessageType] = useState<IMessageTypeListItem>(null);
    const [editor, setEditor] = useState<any>(null);
    const [ckEditorDataId, setCkEditorDataId] = useState(0);
    //const ckEditorContainerDiv = useRef<HTMLDivElement>(null);
    const initializingCkEditorContent = useRef(false);

    useEffect(() => {
        setCkEditorContent(props.content);
    }, [props.messageId]);

    useEffect(() => {
        setMessageType(props.messageTypes.find(i => i.id === props.messageTypeId) ?? props.messageTypes[0]);
    }, [props.messageTypeId]);

    useEffect(() => {
        return function cleanup() {
            destroyCkEditor();
        };
    }, []);

    const onChangeDate = (value: moment.Moment | string, callBack: (dateStr: string) => void) => {
        let dateStr = "";
        if (typeof value === "string") {
            if (!Base.isNullOrEmpty(value)) {
                dateStr = Base.timeToDateStr(value.toDate().getTime());
            }
        } else {
            dateStr = Base.timeToDateStr(value);
        }
        callBack(dateStr);
    };

    // #region CkEditor
    const setCkEditorContent = (value: string) => {
        if (!editor) return;
        initializingCkEditorContent.current = true;
        editor.setData(value, {
            callback: () => {
                editor.resetDirty();
                initializingCkEditorContent.current = false;
            }
        });
        setCkEditorDataId(ckEditorDataId + 1);
    };

    const destroyCkEditor = () => {
        if (!editor) return;
        editor.destroy();
        setEditor(null);
    };

    const handleCkEditorInstanceReady = (ev) => {
        setEditor(ev.editor);
        setCkEditorDataId(ckEditorDataId + 1);
    };

    const handleCkEditorChange = (ev) => {
        if (initializingCkEditorContent.current) return;
        const value: string = ev.editor.getData();
        props.onChangeContent(value, ev.editor.checkDirty());
    };
    // #endregion CkEditor

    // #region TargetUnits
    const handleAddUnit = (unit: IIdTitleNode): Promise<ApiSuccess> => {
        if (!unit) return;
        if (props.messageTargetUnits.find(i => i.unitId === unit.id)) return null;
        const messageTargetUnit = new RecordUnit();
        messageTargetUnit.id = Base.getGuid();
        messageTargetUnit.unitId = unit.id;
        messageTargetUnit.title = unit.title;
        props.onChangeMessageTargetUnits(RecordUnit.sort(props.messageTargetUnits.concat(messageTargetUnit)));
        return Base.getPromiseResult(new ApiSuccess());
    };

    const handleRemoveUnits = (unitIds: string[]): Promise<ApiSuccess> => {
        if (!unitIds || unitIds.length < 1) return null;
        props.onChangeMessageTargetUnits(props.messageTargetUnits.filter(i => unitIds.indexOf(i.unitId) < 0));
        return Base.getPromiseResult(new ApiSuccess());
    };
    // #endregion TargetUnits

    // #region messageTargetCompanyGroups
    const handleAddMessageTargetCompanyGroups = (messageTargetCompanyGroups: IMessageEditItemTargetCompanyGroup[]) => {
        const missingGroups = messageTargetCompanyGroups.filter(i => !props.messageTargetCompanyGroups.find(j => i.code === j.code));
        if (!missingGroups.length) return;
        props.onChangeMessageTargetCompanyGroups(MessageEditItemTargetCompanyGroup.sort(props.messageTargetCompanyGroups.concat(missingGroups)));
    };

    const handleRemoveMessageTargetCompanyGroups = (messageTargetCompanyGroupIds: string[]) => {
        if (!messageTargetCompanyGroupIds || messageTargetCompanyGroupIds.length < 1) return;
        props.onChangeMessageTargetCompanyGroups(props.messageTargetCompanyGroups.filter(i => messageTargetCompanyGroupIds.indexOf(i.id) < 0));
    };
    // #endregion messageTargetCompanyGroups    

    return (
        <>
            <GridRow height="calc(100% + 16px)">
                <Grid item sm={9} height="100%">
                    <VerticalContainer
                        before={<>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Title} margin={0} required>
                                        <TextField name="code" title={Translations.Title} inputProps={{ maxLength: 100 }} value={props.title} disabled={props.isLockedToOtherUser} required={true} onChange={(event) => props.onChangeTitle(event.target.value)} size="small" fullWidth />
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.MessageType} margin={0}>
                                        <Dropdown
                                            selectedTitle={<MessageTypeDropDownLine item={messageType} />}
                                            selectMode={true}
                                            disabled={props.isLockedToOtherUser}
                                            actions={props.messageTypes.map(item => {
                                                return {
                                                    title: <MessageTypeDropDownLine item={item} />,
                                                    selected: props.messageTypeId === item.id,
                                                    onClick: () => props.onChangeMessageType(item.id)
                                                };
                                            })}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.MessageUnit} margin={0}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            disabled={props.isLockedToOtherUser}
                                            singleSelect={true}
                                            selectedIds={[props.unitId]}
                                            showCommentInDropdown={true}
                                            values={props.senderUnits}
                                            onChange={(unitIds: string[]) => props.onChangeUnit(unitIds[0])}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <GridRow>
                                        <Grid item sm={4}>
                                            <FormField title={Translations.Date} required>
                                                <DatePicker
                                                    value={props.dateStr}
                                                    disabled={props.isLockedToOtherUser}
                                                    onChange={(value) => onChangeDate(value, props.onChangeDateStr)}
                                                />
                                            </FormField>
                                        </Grid>
                                        <Grid item sm={4}>
                                            <FormField title={Translations.StartDate} required>
                                                <DatePicker
                                                    value={props.startDateStr}
                                                    disabled={props.isLockedToOtherUser}
                                                    onChange={(value) => onChangeDate(value, props.onChangeStartDateStr)}
                                                />
                                            </FormField>
                                        </Grid>
                                        <Grid item sm={4}>
                                            <FormField title={Translations.MessageEndDate}>
                                                <DatePicker
                                                    value={props.endDateStr}
                                                    disabled={props.isLockedToOtherUser}
                                                    onChange={(value) => onChangeDate(value, props.onChangeEndDateStr)}
                                                />
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={<span>&nbsp;</span>} fullWidth>
                                        <FormCheckBox
                                            title={Translations.RequireReadReceipt}
                                            checked={props.requiresReceipt}
                                            disabled={props.isLockedToOtherUser}
                                            onChange={props.onChangeRequiresReceipt}
                                        />
                                    </FormField>
                                </Grid>
                                {props.sessionState.isSuperAdmin &&
                                    <Grid item sm={3}>
                                        <FormField title={<span>&nbsp;</span>} fullWidth>
                                            <FormCheckBox
                                                title={Translations.SystemAdminMessage}
                                                checked={props.systemAdminMessage}
                                                disabled={props.isLockedToOtherUser}
                                                onChange={props.onChangeSystemAdminMessage}
                                            />
                                        </FormField>
                                    </Grid>
                                }
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Contents} fullWidth>
                                    </FormField>
                                </Grid>
                            </GridRow>
                        </>}
                        main={
                            <AppCkEditor
                                fitToContainerHeight
                                readOnly={props.isLockedToOtherUser}
                                value={props.content}
                                dataId={ckEditorDataId}
                                htmlMode={true}
                                height=""
                                onInstanceReady={handleCkEditorInstanceReady}
                                onChange={handleCkEditorChange}
                            />
                        }
                    />
                </Grid>
                <Grid item sm={3}>
                    <UnitBlocks
                        isReadOnly={props.isLockedToOtherUser || props.systemAdminMessage}
                        margin={"-0.75rem 0 0 0"}
                        title={Translations.TargetsMyCompany}
                        unitTree={props.targetUnits}
                        units={props.messageTargetUnits.map(i => IdTitleNode.createIdTitleNode(i.unitId, i.title, []))}
                        onAddUnit={handleAddUnit}
                        onRemoveUnits={handleRemoveUnits}
                    />
                    {(props.hasNormalCompanyGroups || props.hasFixedCompanyGroups) &&
                        <MessageEditDialogCompanyGroupBlocks
                            isReadOnly={props.isLockedToOtherUser || props.systemAdminMessage}
                            companyId={props.companyId}
                            hasNormalCompanyGroups={props.hasNormalCompanyGroups}
                            hasFixedCompanyGroups={props.hasFixedCompanyGroups}
                            canUseAllCompanyGroup={props.canUseAllCompanyGroup}
                            title={Translations.TargetsOtherCompanies}
                            messageTargetCompanyGroups={props.messageTargetCompanyGroups}
                            onAddMessageTargetCompanyGroups={handleAddMessageTargetCompanyGroups}
                            onRemoveMessageTargetCompanyGroups={handleRemoveMessageTargetCompanyGroups}
                        />
                    }
                </Grid>
            </GridRow>
        </>
    );
};
